import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LearnHutLogo from "../Logos To be used/learnhut_logo_recolored.png";
import { uploadTextToCloudinary } from "../utils/cloudinaryUpload";
import "./PhaseTwo.css";

const PLANNING_DEADLINE_LABEL = "01.09.2026";
const CLOUDINARY_SUBMISSIONS_FOLDER = "phase-two-interests";
const CLOUDINARY_SUBMISSION_TAGS = ["phase-two-interest", "learnhut"];

const initialForm = {
  email: "",
  subjects: "",
  note: "",
  privacy: false,
};

const highlights = [
  {
    title: "KI-gestützte Inhalte auf Basis Ihrer Materialien",
    copy: "Vorlesungsfolien, Skripte und Übungsmaterialien können in strukturierte Lerneinheiten überführt werden.",
  },
  {
    title: "Übungsaufgaben, Quizze und Klausurvorbereitung",
    copy: "LearnHut ergänzt Lehrmaterialien um fachbezogene Aufgaben, Quizfragen und Beispiele zur Prüfungsvorbereitung.",
  },
  {
    title: "Feedback aus der Nutzung",
    copy: "Studierende können Rückmeldungen geben, damit unklare Stellen und technische Probleme früh sichtbar werden.",
  },
];

const isEmail = (value) => /\S+@\S+\.\S+/.test(String(value || "").trim());

const sanitizeFilenamePart = (value) => {
  const normalized = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

  return normalized || "unknown";
};

const buildSubmissionFilename = (email, submittedAt) => {
  const timestamp = submittedAt.replace(/[:.]/g, "-");
  return `phase-two-${timestamp}-${sanitizeFilenamePart(email)}.txt`;
};

const buildSubmissionText = ({ form, source, subjects, submittedAt }) => [
  "LearnHut Phase Two Interesse",
  "",
  `Timestamp: ${submittedAt}`,
  `Email: ${form.email.trim()}`,
  `Source: ${source}`,
  `Planning deadline: ${PLANNING_DEADLINE_LABEL}`,
  "Consent: yes",
  "",
  "Fächer / Lehrveranstaltungen:",
  ...subjects.map((subject) => `- ${subject}`),
  "",
  "Anmerkung:",
  form.note.trim() || "-",
].join("\n");

function PhaseTwo() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [submitError, setSubmitError] = useState("");

  const subjectValues = useMemo(
    () => String(form.subjects || "")
      .split(/\r?\n|;/)
      .map((subject) => subject.trim())
      .filter(Boolean),
    [form.subjects]
  );

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setStatus("");
    setSubmitError("");
  };

  const validate = () => {
    const nextErrors = {};
    if (!isEmail(form.email)) nextErrors.email = "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
    if (subjectValues.length === 0) {
      nextErrors.subjects = "Bitte nennen Sie mindestens ein Fach oder eine Lehrveranstaltung.";
    }
    if (!form.privacy) {
      nextErrors.privacy = "Bitte bestätigen Sie, dass wir Ihre Angaben zur Kontaktaufnahme nutzen dürfen.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate() || status === "submitting") return;

    setStatus("submitting");
    setSubmitError("");

    const submittedAt = new Date().toISOString();
    const source =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "unknown";

    const submissionText = buildSubmissionText({
      form,
      source,
      subjects: subjectValues,
      submittedAt,
    });

    try {
      await uploadTextToCloudinary(submissionText, {
        folder: CLOUDINARY_SUBMISSIONS_FOLDER,
        tags: CLOUDINARY_SUBMISSION_TAGS,
        filename: buildSubmissionFilename(form.email, submittedAt),
      });

      setStatus("saved");
    } catch (error) {
      setStatus("error");
      setSubmitError(error?.message || "Cloudinary konnte die Textdatei nicht speichern.");
    }
  };

  return (
    <main className="phase-two-page">
      <header className="phase-two-nav">
        <Link to="/phase-two" className="phase-two-logo-link" aria-label="LearnHut Phase Two">
          <img src={LearnHutLogo} alt="LearnHut" />
        </Link>
        <nav aria-label="Phase Two Navigation">
          <Link to="/phase-two/faq">FAQ</Link>
        </nav>
      </header>

      <section className="phase-two-hero">
        <div className="phase-two-hero-copy">
          <span className="phase-two-eyebrow">Für Lehrende</span>
          <h1>LearnHut für Ihre Lehrveranstaltung in der zweiten Testphase.</h1>
          <p>
            Wir planen die nächste LearnHut-Testphase mit weiteren Fächern. Wenn
            Sie LearnHut für eine Lehrveranstaltung testen möchten, können Sie
            uns direkt hier Ihre E-Mail-Adresse und die geplanten Fächer nennen.
          </p>
          <p className="phase-two-deadline">
            Für eine gute Semesterplanung freuen wir uns über eine erste Liste
            möglichst bis zum <strong>{PLANNING_DEADLINE_LABEL}</strong>.
          </p>
        </div>

        <form className="phase-two-form-card phase-two-hero-form-card" id="interest-form" onSubmit={handleSubmit}>
          <div className="phase-two-form-card-head">
            <span className="phase-two-eyebrow">Interesse anmelden</span>
            <h2>Fächerliste senden</h2>
            <p>
              Tragen Sie Ihre E-Mail-Adresse und die Lehrveranstaltungen ein,
              die Sie für die LearnHut-Testphase bereitstellen möchten.
            </p>
          </div>

          <label>
            <span>E-Mail-Adresse</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              placeholder="name@hochschule.de"
              aria-invalid={Boolean(errors.email)}
            />
            {errors.email ? <small className="phase-two-error">{errors.email}</small> : null}
          </label>

          <div className="phase-two-subjects-field">
            <span>Fächer / Lehrveranstaltungen</span>
            <textarea
              rows="4"
              value={form.subjects}
              onChange={(event) => updateField("subjects", event.target.value)}
              placeholder={"z.B. Technische Mechanik 1\nThermodynamik\nMathematik für Ingenieure"}
              aria-invalid={Boolean(errors.subjects)}
            />
            {errors.subjects ? <small className="phase-two-error">{errors.subjects}</small> : null}
          </div>

          <label>
            <span>Anmerkung optional</span>
            <textarea
              rows="3"
              value={form.note}
              onChange={(event) => updateField("note", event.target.value)}
              placeholder="Zeitraum, Materialstand, besondere Hinweise..."
            />
          </label>

          <label className="phase-two-privacy">
            <input
              type="checkbox"
              checked={form.privacy}
              onChange={(event) => updateField("privacy", event.target.checked)}
            />
            <span>
              Ich bin einverstanden, dass LearnHut diese Angaben zur Kontaktaufnahme
              und Planung der Testphase nutzt.
            </span>
          </label>
          {errors.privacy ? <small className="phase-two-error">{errors.privacy}</small> : null}

          <div className="phase-two-submit-row phase-two-submit-row-single">
            <button type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Wird gespeichert..." : "Angaben speichern"}
            </button>
          </div>

          {status === "saved" ? (
            <div className="phase-two-storage-note phase-two-storage-note-success" role="status">
              <strong>Gespeichert.</strong>
              <p>
                Vielen Dank. Ihre Angaben wurden erfolgreich übermittelt.
              </p>
            </div>
          ) : null}

          {status === "error" ? (
            <div className="phase-two-storage-note phase-two-storage-note-error" role="alert">
              <strong>Speichern war gerade nicht möglich.</strong>
              <p>{submitError}</p>
            </div>
          ) : null}
        </form>
      </section>

      <section className="phase-two-highlight-grid" aria-label="LearnHut Vorteile">
        {highlights.map((item) => (
          <article className="phase-two-highlight" key={item.title}>
            <span aria-hidden="true" />
            <h2>{item.title}</h2>
            <p>{item.copy}</p>
          </article>
        ))}
      </section>
    </main>
  );
}

export default PhaseTwo;
